/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { getPathFromUrl } from "@eisinc/App/Utilities/helpers";
import Copy from "@eisinc/XDL/Icons/Copy";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsCopyLinkStyles {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const copyLinkStyles: ProductDetailsCopyLinkStyles = {
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    link: {
        icon: {
            iconProps: {
                src: Copy,
                size: 18,
                color: "primary.main",
            },
        },
        color: "text.main",
        typographyProps: {
            size: 14,
            lineHeight: "20px",
        },
    },
};

const styles = copyLinkStyles;

const ProductDetailsCopyLink: React.FC<Props> = ({ product, variantSelectionCompleted }) => {
    const [isCopied, setIsCopies] = useState(false);

    const copyLinkClickHandler = () => {
        let url = getPathFromUrl(window.location.href);
        const el = document.createElement("textarea");

        if (variantSelectionCompleted) {
            url += `?option=${product.productNumber}`;
        }

        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);

        setIsCopies(true);

        setTimeout(() => {
            setIsCopies(false);
        }, 1000 * 2);
    };

    if (!product) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link {...styles.link} onClick={copyLinkClickHandler} data-test-selector="productCopyLink">
                {isCopied && translate("Copied!")}
                {!isCopied && translate("Copy Page Link")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsCopyLink)),
    definition: {
        displayName: "Copy Link",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
